// © Microsoft Corporation. All rights reserved.

import { AccentButton, OutlineButton } from "@microsoft/fast-components-react-msft";
import { Activities, ActivityData, User } from "@msnews/social-data-service";
import { CommentConnector, ComponentConnector, FollowInfo, PopupConnector, UsersConnector } from "../../Social.props";

import ProfileAlertActivity from "./ProfileAlertActivity";
import ProfileBadgeActivity from "./ProfileBadgeActivity";
import ProfileFollowActivity from "./ProfileFollowActivity";
import ProfileReactionActivity from "./ProfileReactionActivity";
import ProfileReplyActivity from "./ProfileReplyActivity";
import React from "react";
import { SocialClassNameContract } from "../../Social.classnames";
import { SocialLocalizedStrings } from "../../Social.localizedStrings";
import { SocialState } from "../../Social.state";
import { TelemetryObjectBase } from "@msnews/telemetry-contracts";
import { classNames } from "@microsoft/fast-web-utilities";
import { connect } from "react-redux";
import { enums } from "../../utils/enums";
import { hasThemeBase } from "../../utils/themeHelpers";
import loadObserver from "../../utils/ObserverHelper";
import openEditProfilePage from "../../utils/openEditProfilePage";
import openGuidelinePage from "../../utils/openGuidelinePage";
import { profileImages } from "../../utils/images";

export interface ProfileActivityProps {
    componentConnector: ComponentConnector;
    t: SocialLocalizedStrings;
    user: User;
    usersConnector: UsersConnector;
    users: { [id: string]: FollowInfo;}; // dictionary definening
    empty: Array<ActivityData>;
    managedClasses: SocialClassNameContract;
    market: string;
    me: User;
    activities: Activities;
    popupConnector: PopupConnector;
    commentConnector: CommentConnector;
    mainTelemetryObject: TelemetryObjectBase;
    theme?: string;
    responsiveClass: string;
}

const mapStateToProps = function(state: SocialState) {
    return {
        me: state.me,
        activities: state.activities,
        theme: state.theme
    };
};

export class ProfileActivity extends React.Component<ProfileActivityProps> {
    profileActivityListContainerRef: React.RefObject<HTMLUListElement>;
    footerRef: React.RefObject<HTMLLIElement>;
    observer: IntersectionObserver;
    isLoading: boolean;

    constructor(props) {
        super(props);
        this.profileActivityListContainerRef = React.createRef();
        this.footerRef = React.createRef();
        loadObserver(() => {
            this.observer = new IntersectionObserver(this.footerInView.bind(this), {
                threshold: 1.0
            });
        });
    }

    async componentDidMount() {
        this.isLoading = true;
        await this.props.usersConnector.fetchActivities();
        this.isLoading = false;

        this.footerRef.current && this.observer.observe(this.footerRef.current);
    }

    async footerInView(entries) {
        if (entries[0].isIntersecting && !this.isLoading && this.props.activities && this.props.activities.hasMore) {
            this.isLoading = true;
            await this.props.usersConnector.fetchActivities();
            this.isLoading = false;
        }
    }

    componentWillUnmount() {
        this.footerRef.current && this.observer.unobserve(this.footerRef.current);
    }

    requestReview(commentId, activityId) {
        this.props.commentConnector.appealComment(commentId, activityId);
    }

    requestUserAppeal(activityId) {
        this.props.usersConnector.appealMe(activityId);
    }

    requestUserPictureAppeal(activityId) {
        this.props.usersConnector.appealMyPicture(activityId);
    }

    render() {
        const { responsiveClass, componentConnector, user, t, managedClasses, users, usersConnector, market, me, activities, popupConnector, mainTelemetryObject, theme } = this.props;
        const isSuperApp = hasThemeBase(theme, enums.theme.superappBase);
        if (!user || !activities) { return null; }

        return (
            activities.list.length > 0
                ?   <ul className={classNames(
                    managedClasses.profileActivityListContainer,
                    [managedClasses.profileActivityListSuperAppContainer, isSuperApp],
                    responsiveClass
                )}

                ref={this.profileActivityListContainerRef}>
                    {activities.list.filter(x => !x.skipRender).map(activity => {
                        const propsToPass = {responsiveClass, componentConnector, activity, t, managedClasses, users, usersConnector, me, market, popupConnector, mainTelemetryObject };
                        switch (activity.type) {
                            case enums.activityType.follow:
                                return <ProfileFollowActivity key={activity.id} {...propsToPass}/>;

                            case enums.activityType.badge:
                                return <ProfileBadgeActivity key={activity.id} {...propsToPass}/>;

                            case enums.activityType.reply:
                                return <ProfileReplyActivity key={activity.id} {...propsToPass}/>;

                            case enums.activityType.reaction:
                                return <ProfileReactionActivity key={activity.id} {...propsToPass}/>;

                            case enums.activityType.moderation: {
                                switch (activity.moderationErrorType) {
                                    case enums.moderationErrorType.takedown:
                                        return <ProfileAlertActivity key={activity.id} {...propsToPass}>
                                            { activity.target && activity.target.body  && <p className="cwt-preview">{ activity.target.body }</p>}
                                            <div className="cwt-buttons">
                                                <AccentButton className="cwt-fast-button" {...openGuidelinePage(componentConnector, theme, market, "_blank")}> {t.ProfileActivityViewGuidelines}</AccentButton>
                                                {activity.appealSent
                                                    ? <OutlineButton className="cwt-fast-button" disabled={true}>
                                                        {t.ProfileActivityRequestReceived}
                                                    </OutlineButton>
                                                    : <OutlineButton className="cwt-fast-button" onClick={this.requestReview.bind(this, activity.target.id, activity.id)}>
                                                        {t.ProfileActivityRequestReview}
                                                    </OutlineButton>
                                                }
                                            </div>
                                        </ProfileAlertActivity>;

                                    case enums.moderationErrorType.ban:
                                        return <ProfileAlertActivity key={activity.id} {...propsToPass}>
                                            <div className="cwt-buttons">
                                                <AccentButton className="cwt-fast-button" {...openGuidelinePage(componentConnector, theme, market, "_blank")}>{t.ProfileActivityViewGuidelines}</AccentButton>
                                                {activity.appealSent
                                                    ? <OutlineButton className="cwt-fast-button" disabled={true}>
                                                        {t.ProfileActivityRequestReceived}
                                                    </OutlineButton>
                                                    : <OutlineButton className="cwt-fast-button" onClick={this.requestUserAppeal.bind(this, activity.id)}>
                                                        {t.ProfileActivityRequestReview}
                                                    </OutlineButton>
                                                }
                                            </div>
                                        </ProfileAlertActivity>;

                                    case enums.moderationErrorType.notAllowName:
                                        return <ProfileAlertActivity key={activity.id} {...propsToPass}>
                                            <div className="cwt-buttons">
                                                <AccentButton className="cwt-fast-button" {...openGuidelinePage(componentConnector, theme, market, "_blank")}> {t.ProfileActivityViewGuidelines}</AccentButton>
                                                {activity.appealSent
                                                    ? <OutlineButton className="cwt-fast-button" disabled={true}>
                                                        {t.ProfileActivityRequestReceived}
                                                    </OutlineButton>
                                                    : <OutlineButton className="cwt-fast-button" onClick={this.requestUserAppeal.bind(this, activity.id)}>
                                                        {t.ProfileActivityRequestReview}
                                                    </OutlineButton>
                                                }

                                                <OutlineButton className="cwt-fast-button" onClick={() => openEditProfilePage(theme, market, "_blank")}> {t.ProfileActivityEditProfile}</OutlineButton>
                                            </div>
                                        </ProfileAlertActivity>;
                                    case enums.moderationErrorType.notAllowPicture:
                                        return <ProfileAlertActivity key={activity.id} {...propsToPass}>
                                            <div className="cwt-buttons">
                                                <AccentButton className="cwt-fast-button" {...openGuidelinePage(componentConnector, theme, market, "_blank")}> {t.ProfileActivityViewGuidelines}</AccentButton>
                                                {activity.appealSent
                                                    ? <OutlineButton className="cwt-fast-button" disabled={true}>
                                                        {t.ProfileActivityRequestReceived}
                                                    </OutlineButton>
                                                    : <OutlineButton className="cwt-fast-button" onClick={this.requestUserPictureAppeal.bind(this, activity.id)}>
                                                        {t.ProfileActivityRequestReview}
                                                    </OutlineButton>
                                                }
                                                <OutlineButton className="cwt-fast-button" onClick={() => openEditProfilePage(theme, market, "_blank")}> {t.ProfileActivityEditProfile}</OutlineButton>
                                            </div>
                                        </ProfileAlertActivity>;
                                    case enums.moderationErrorType.rejectUserAppeal:
                                    case enums.moderationErrorType.rejectProfilePicAppeal:
                                    case enums.moderationErrorType.rejectCommentAppeal:
                                        return <ProfileAlertActivity key={activity.id} {...propsToPass}>
                                            <div className="cwt-buttons">
                                                <AccentButton className="cwt-fast-button" {...openGuidelinePage(componentConnector, theme, market, "_blank")}>{t.ProfileActivityViewGuidelines}</AccentButton>
                                            </div>
                                        </ProfileAlertActivity>;
                                    case enums.moderationErrorType.approveUserAppeal:
                                    case enums.moderationErrorType.approveCommentAppeal:
                                    case enums.moderationErrorType.approveProfilePicAppeal:
                                        return <ProfileAlertActivity key={activity.id} {...{...propsToPass, checkIcon: true}} />;
                                }
                            }
                        }
                    })}
                    <li ref={this.footerRef}/>
                </ul>
                :   <div className={classNames(
                    managedClasses.profileEmptyTabContainer,
                    [managedClasses.profileEmptyTabSuperAppContainer, isSuperApp]
                )}>
                    <img src={profileImages.zeroActivity} alt={t.ImgAltsZeroActivityProfile}/>
                    <div className="cwt-message"> {t.ProfileActivityZeroActivityMessage} </div>
                    <div className="cwt-sub-message"> {t.ProfileActivityZeroActivitySubMessage} </div>
                </div>
        );
    }
}

export default connect(mapStateToProps)(ProfileActivity);